import { getUserInfo } from './auth';
import { getApiV1Url } from '../config/api';

export interface BoardComment {
  projectCode: string;
  boardCode: string;
  postCode: string;
  code: string;
  status: string;
  sortOrder?: string;
  depth?: number;
  content: string;
  likeCount: number;
  dislikeCount: number;
  replyCount: number;
  createdBy?: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface GetCommentListParams {
  cursor?: string;
  size?: number;
  sinceAt?: string;
  activeOnly?: boolean;
}

export interface CommentListResult {
  comments: BoardComment[];
  nextCursor?: string;
  hasNext: boolean;
  newCommentCount?: number;
}

export interface CommentThreadResult {
  comments: BoardComment[];
}

export interface BoardCommentCreateRequest {
  parentCode?: string;
  content: string;
}

export interface BoardCommentUpdateRequest {
  content: string;
}

interface ApiErrorBody {
  timestamp?: number;
  errors?: {
    error?: Array<{ code: number; target: string; message: string }>;
  };
  message?: string;
  data?: unknown;
}

function getAuthHeaders(): Record<string, string> {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }
  return {
    'Content-Type': 'application/json',
    'X-Account-Code': userInfo.accountCode,
    'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
  };
}

function commentsBasePath(
  projectCode: string,
  boardCode: string,
  postCode: string
): string {
  return `/projects/${projectCode}/boards/${encodeURIComponent(boardCode)}/posts/${encodeURIComponent(postCode)}/comments`;
}

async function parseApiResponse<T>(response: Response): Promise<T> {
  const result: ApiErrorBody = await response.json().catch(() => ({}));

  if (result.errors?.error && result.errors.error.length > 0) {
    const errorMessage = result.errors.error.map((e) => e.message).join(', ');
    throw new Error(errorMessage);
  }

  if (!response.ok) {
    throw new Error(result.message || `HTTP error! status: ${response.status}`);
  }

  return result as T;
}

function normalizeComment(raw: Record<string, unknown>): BoardComment {
  return {
    projectCode: String(raw.projectCode ?? ''),
    boardCode: String(raw.boardCode ?? ''),
    postCode: String(raw.postCode ?? ''),
    code: String(raw.code ?? ''),
    status: String(raw.status ?? ''),
    sortOrder: raw.sortOrder != null ? String(raw.sortOrder) : undefined,
    depth: raw.depth != null ? Number(raw.depth) : undefined,
    content: String(raw.content ?? ''),
    likeCount: Number(raw.likeCount ?? raw.like_count ?? 0) || 0,
    dislikeCount: Number(raw.dislikeCount ?? raw.dislike_count ?? 0) || 0,
    replyCount: Number(raw.replyCount ?? raw.reply_count ?? 0) || 0,
    createdBy: typeof raw.createdBy === 'string' ? raw.createdBy : undefined,
    createdAt: String(raw.createdAt ?? raw.created_at ?? ''),
    updatedBy: typeof raw.updatedBy === 'string' ? raw.updatedBy : undefined,
    updatedAt:
      typeof raw.updatedAt === 'string'
        ? raw.updatedAt
        : typeof raw.updated_at === 'string'
          ? raw.updated_at
          : undefined,
  };
}

function extractCommentList(data: unknown): CommentListResult {
  if (!data || typeof data !== 'object') {
    return { comments: [], hasNext: false };
  }
  const obj = data as Record<string, unknown>;
  const list = obj.comments ?? obj.items ?? obj.list;
  const comments = Array.isArray(list)
    ? list.map((item) => normalizeComment(item as Record<string, unknown>))
    : [];
  return {
    comments,
    nextCursor: typeof obj.nextCursor === 'string' ? obj.nextCursor : undefined,
    hasNext: Boolean(obj.hasNext),
    newCommentCount:
      obj.newCommentCount != null ? Number(obj.newCommentCount) : undefined,
  };
}

function extractCommentThread(data: unknown): CommentThreadResult {
  if (!data || typeof data !== 'object') {
    return { comments: [] };
  }
  const obj = data as Record<string, unknown>;
  const list = obj.comments ?? obj.items ?? obj.list;
  const comments = Array.isArray(list)
    ? list.map((item) => normalizeComment(item as Record<string, unknown>))
    : [];
  return { comments };
}

/**
 * 댓글 목록 (루트 댓글, 커서 페이징)
 * GET /api/v1/projects/{projectCode}/boards/{boardCode}/posts/{postCode}/comments
 */
export const getCommentList = async (
  projectCode: string,
  boardCode: string,
  postCode: string,
  params?: GetCommentListParams
): Promise<CommentListResult> => {
  const queryParams = new URLSearchParams();
  if (params?.cursor) queryParams.append('cursor', params.cursor);
  if (params?.size != null) queryParams.append('size', String(params.size));
  if (params?.sinceAt) queryParams.append('sinceAt', params.sinceAt);
  if (params?.activeOnly != null) {
    queryParams.append('activeOnly', String(params.activeOnly));
  } else {
    queryParams.append('activeOnly', 'true');
  }

  const qs = queryParams.toString();
  const url = getApiV1Url(`${commentsBasePath(projectCode, boardCode, postCode)}?${qs}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const result = await parseApiResponse<ApiErrorBody>(response);
  return extractCommentList(result.data);
};

/**
 * 댓글 스레드 (자식 댓글)
 * GET .../comments/thread/{commentCode}
 */
export const getCommentThread = async (
  projectCode: string,
  boardCode: string,
  postCode: string,
  commentCode: string
): Promise<CommentThreadResult> => {
  const url = getApiV1Url(
    `${commentsBasePath(projectCode, boardCode, postCode)}/thread/${encodeURIComponent(commentCode)}`
  );
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const result = await parseApiResponse<ApiErrorBody>(response);
  return extractCommentThread(result.data);
};

/**
 * 댓글 생성
 * POST .../comments
 */
export const createComment = async (
  projectCode: string,
  boardCode: string,
  postCode: string,
  body: BoardCommentCreateRequest
): Promise<BoardComment> => {
  const url = getApiV1Url(commentsBasePath(projectCode, boardCode, postCode));
  const payload: BoardCommentCreateRequest = {
    content: body.content.trim(),
    ...(body.parentCode?.trim() ? { parentCode: body.parentCode.trim() } : {}),
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  const result = await parseApiResponse<ApiErrorBody>(response);
  const raw = result.data;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return normalizeComment(raw as Record<string, unknown>);
  }
  throw new Error('댓글 생성 응답을 처리할 수 없습니다.');
};

/**
 * 댓글 수정
 * POST .../comments/{commentCode}
 */
export const updateComment = async (
  projectCode: string,
  boardCode: string,
  postCode: string,
  commentCode: string,
  body: BoardCommentUpdateRequest
): Promise<BoardComment> => {
  const url = getApiV1Url(
    `${commentsBasePath(projectCode, boardCode, postCode)}/${encodeURIComponent(commentCode)}`
  );
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ content: body.content.trim() }),
  });
  const result = await parseApiResponse<ApiErrorBody>(response);
  const raw = result.data;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return normalizeComment(raw as Record<string, unknown>);
  }
  throw new Error('댓글 수정 응답을 처리할 수 없습니다.');
};

/**
 * 댓글 삭제
 * POST .../comments/{commentCode}/delete
 */
export const deleteComment = async (
  projectCode: string,
  boardCode: string,
  postCode: string,
  commentCode: string
): Promise<void> => {
  const url = getApiV1Url(
    `${commentsBasePath(projectCode, boardCode, postCode)}/${encodeURIComponent(commentCode)}/delete`
  );
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  await parseApiResponse<ApiErrorBody>(response);
};